import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle } from 'lucide-react';

// Simple promise-based pub/sub
const listeners = new Set();
let nextId = 0;

/**
 * Ask the user a yes/no question via a themed modal.
 * Returns a Promise that resolves with `true` (confirm) or `false` (cancel/close).
 */
export const showAppConfirm = (message, options = {}) =>
  new Promise((resolve) => {
    const payload = {
      id: ++nextId,
      message: String(message ?? ''),
      title: options.title || 'Konfirmasi',
      confirmLabel: options.confirmLabel || 'OK',
      cancelLabel: options.cancelLabel || 'Batal',
      resolve,
    };
    listeners.forEach((l) => l(payload));
  });

export default function AppConfirm() {
  const [item, setItem] = useState(null);
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    const handler = (payload) => setItem(payload);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  useEffect(() => {
    if (item && confirmBtnRef.current) confirmBtnRef.current.focus();
  }, [item]);

  const close = (result) => {
    if (item) item.resolve(result);
    setItem(null);
  };

  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => close(false)}
          data-testid="app-confirm-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            data-testid="app-confirm-dialog"
          >
            <div className="flex items-start gap-4 p-6">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <HelpCircle size={22} />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="font-heading font-semibold text-slate-900 text-base mb-1">
                  {item.title}
                </h3>
                <p
                  className="text-sm text-slate-600 leading-relaxed whitespace-pre-line"
                  data-testid="app-confirm-message"
                >
                  {item.message}
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2 flex justify-end gap-3">
              <button
                onClick={() => close(false)}
                className="btn-secondary min-w-[88px]"
                data-testid="app-confirm-cancel-btn"
              >
                {item.cancelLabel}
              </button>
              <button
                ref={confirmBtnRef}
                onClick={() => close(true)}
                className="btn-primary min-w-[88px]"
                data-testid="app-confirm-ok-btn"
              >
                {item.confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
